"use client";

import { useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { useAccountReadiness } from "@/providers/account-readiness";
import { tables } from "@/module_bindings";
import { mapUser } from "@/lib/stdb-map";
import type { User } from "@/types/user";

/**
 * `my_user` is a caller-scoped view — at most one row. `User.organizations`
 * is legacy-shape residue (see `mapUser`'s doc comment); this hook is the
 * one place that fills it, from `my_organizations`' ids.
 */
export function useCurrentUser(): { user: User; isReady: boolean } {
  const accountReady = useAccountReadiness();
  const [userRows, userReady] = useTable(tables.my_user);
  const [orgRows, orgsReady] = useTable(tables.my_organizations);

  const user = useMemo<User>(() => {
    const row = userRows[0];
    if (!row) return null;
    // Non-null assertion: mapUser never actually returns null for a row —
    // the `User | null` type exists for "no user" call sites, not this one.
    return { ...mapUser(row)!, organizations: orgRows.map((org) => org.id) };
  }, [userRows, orgRows]);

  return { user, isReady: accountReady || (userReady && orgsReady) };
}
